import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import pick from 'lodash/pick';
import { formatString } from '@pes-ui/utils';
import { LOAD_PLACEHOLDER, NOT_SPECIFIED_PROPERTY_VAL, REQUIRED_RUNTIME_PARAMS, RUNTIME_ERROR_CODES, RUNTIME_FIELDS, RUNTIME_LABEL_CODES, } from './constants';
export function parseDecimal(value) {
  return parseInt(value, 10);
}
export function calculateMaxCapacity(inputValue, pwrRated) {
  const value = parseDecimal(inputValue);
  return Math.round((value / pwrRated) * 100);
}
export function calculateRuntimeMinutes(runtimeParams, calculatedValue) {
  const { vah, bcurveComp, bcurveExpo, bcurveSlope, bcurveScale, ptare } = runtimeParams;
  const mathPow = (vah / (calculatedValue + ptare)) ** bcurveExpo;
  return (60 * bcurveScale * (mathPow - bcurveSlope + (bcurveComp * (calculatedValue + ptare)) / vah));
}
export function isMaxCapacityExceeded(value, pwrRated) {
  return parseDecimal(value) > pwrRated;
}
export function isLoadLessThanMinimum(value, min) {
  return parseDecimal(value) < min;
}
export function getMinValue(max, min) {
  return min !== null && min !== void 0 ? min : Math.ceil(max * 0.1);
}
export function calculateRuntime(runtimeParams, value) {
  if (!value) {
    return null;
  }
  if (isMaxCapacityExceeded(value, runtimeParams.pwrRated)) {
    return null;
  }
  if (isLoadLessThanMinimum(value, getMinValue(runtimeParams.pwrRated, runtimeParams.startPoint))) {
    return null;
  }
  const runtime = calculateRuntimeMinutes(runtimeParams, parseDecimal(value));
  if (Number.isNaN(runtime) || runtime <= 0) {
    return null;
  }
  const resultRuntime = {};
  const runtimeMinutes = Math.floor(runtime);
  if (runtimeMinutes < 30) {
    resultRuntime.minutes = runtimeMinutes;
    resultRuntime.seconds = Math.floor((runtime - runtimeMinutes) * 60) || null;
  }
  if (runtimeMinutes >= 30 && runtimeMinutes < 60) {
    resultRuntime.minutes = runtimeMinutes;
  }
  if (runtimeMinutes >= 60) {
    resultRuntime.hours = Math.floor(runtimeMinutes / 60);
    resultRuntime.minutes = Math.floor(runtimeMinutes % 60) || null;
  }
  return omitBy(resultRuntime, isNil);
}
export function stringifyRuntime(runtime, labels, defaultValue = NOT_SPECIFIED_PROPERTY_VAL) {
  if (isEmpty(runtime)) {
    return defaultValue;
  }
  return [RUNTIME_FIELDS.HOURS, RUNTIME_FIELDS.MINUTES, RUNTIME_FIELDS.SECONDS]
    .filter((field) => !isNil(runtime[field]))
    .map((field) => `${runtime[field]}${labels[field]}`)
    .join(' ');
}
export function hasNullParams(runtimeParams) {
  const paramsValue = Object.values(pick(runtimeParams, REQUIRED_RUNTIME_PARAMS));
  return paramsValue.some(isNil);
}
export function validate(runtimeParams, value) {
  const { pwrRated, startPoint } = runtimeParams !== null && runtimeParams !== void 0 ? runtimeParams : {};
  if (hasNullParams(runtimeParams)) {
    return RUNTIME_ERROR_CODES.NULL_PARAM;
  }
  if (!value) {
    return RUNTIME_ERROR_CODES.EMPTY;
  }
  if (isMaxCapacityExceeded(value, pwrRated)) {
    return RUNTIME_ERROR_CODES.EXCEED_MAX_CAPACITY;
  }
  if (isLoadLessThanMinimum(value, getMinValue(pwrRated, startPoint))) {
    return RUNTIME_ERROR_CODES.LESS_THAN_MINIMUM;
  }
  return null;
}
export function formatMessage(message, labelCode, runtimeParams) {
  if (!message || !labelCode || isEmpty(runtimeParams)) {
    return message !== null && message !== void 0 ? message : null;
  }
  let load;
  const { pwrRated, startPoint } = runtimeParams;
  switch (labelCode) {
    case RUNTIME_LABEL_CODES.TOTAL_LOAD_EXCEEDS:
      load = pwrRated;
      break;
    case RUNTIME_LABEL_CODES.ENTERED_LOAD_LESS_MINIMUM:
      load = getMinValue(pwrRated, startPoint);
      break;
  }
  if (!load) {
    return message;
  }
  return formatString(message, {
    [LOAD_PLACEHOLDER]: load === null || load === void 0 ? void 0 : load.toString(),
  });
}
